/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect, useState, useCallback, useRef } from "react";

import classnames from "classnames";
import Cropper, { Point, Area } from "react-easy-crop";
import { useTranslation } from "react-i18next";
import { FiUpload } from "react-icons/fi";

import { Logo } from "../../../models/company";
import { File as IFile } from "../../../models/file";
import { base, convertToBase64 } from "../../../util/convertToBase64";
import { uploadBase64ImageToS3 } from "../../../util/uploadBase64ImageToS3";
import { fileIsValid } from "../../../util/fileIsValid";
import Modal from "../../Generic/Modal";
import { NotificationToast } from "../../Generic/Toast/NotificationToast";
import Button, { Color } from "../Button";
import { getCroppedImg } from "./cropper";
import useStyles from "./styles";
import isBase64 from "is-base64";

const MAX_ALLOWED_SIZE_MB = 2;

interface LogoCompanyProps {
  id: string;
  title: string;
  description?: string;
  onChangeLogo?: (logo: Logo | undefined) => void;
  logo?: Logo;
  maxAllowedSizeMb?: number;
  allowedImageExtensions?: string[];
  classname?: string;
  cropImage?: boolean;
  cropAspect?: number;
  cropAspectHeight?: number;
  cropAspectWidth?: number;
  cropShape?: "round" | "rect";
  disabled?: boolean;
  showActionButtons?: boolean;
  hideFooter?: boolean;
  error?: boolean;
  direction?: "horizontal" | "vertical";
  uploadMode?: "base64" | "s3";
  uploadPathPrefix?: string;
}

const ImageInput = (props: LogoCompanyProps) => {
  const { t: translate } = useTranslation();

  const {
    id,
    title,
    onChangeLogo,
    logo,
    maxAllowedSizeMb,
    allowedImageExtensions,
    classname,
    cropImage = false,
    cropAspect,
    cropAspectHeight,
    cropAspectWidth,
    description = translate("addLogo"),
    cropShape = "rect",
    disabled,
    showActionButtons,
    hideFooter,
    error,
    direction = "horizontal",
    uploadMode = "base64",
    uploadPathPrefix,
  } = props;

  const classes = useStyles();
  const inputImageRef = useRef<HTMLInputElement>(null);

  const [_extension, _setExtension] = useState<string>("");

  const [openCropperModal, setOpenCropperModal] = useState<boolean>(false);

  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleImageCrop = async () => {
    const image = await getCroppedImg(logoToCrop, croppedAreaPixels, _extension, cropShape);
    const imageString = String(image).replace(base(_extension), "");

    if (!image || !onChangeLogo) {
      setOpenCropperModal(false);
      return;
    }

    _setLogo(String(image));

    if (uploadMode === "s3") {
      if (!imageString) {
        NotificationToast.error(translate("anErrorOccurredTryAgain"));
        return;
      }

      setIsUploading(true);

      try {
        const link = await uploadBase64ImageToS3(imageString, _extension || "png", {
          pathPrefix: uploadPathPrefix || "logo",
        });

        let newLogo: Logo = {};

        if (logo) {
          newLogo = {
            ...logo,
            mediaLink: link,
          };

          delete newLogo?.createdAt;
          delete newLogo?.updatedAt;

          onChangeLogo(newLogo);
        } else {
          onChangeLogo({ mediaLink: link });
        }

        _setLogo(link);
        setOpenCropperModal(false);
      } catch (error) {
        console.error("Error uploading cropped image to S3", error);
        NotificationToast.error(translate("anErrorOccurredTryAgain"));
      } finally {
        setIsUploading(false);
      }

      return;
    }

    let newLogo: Logo = {};

    if (logo) {
      newLogo = {
        ...logo,
        mediaLink: imageString,
      };

      delete newLogo?.createdAt;
      delete newLogo?.updatedAt;

      onChangeLogo(newLogo);
    } else {
      onChangeLogo({ mediaLink: imageString });
    }

    setOpenCropperModal(false);
  };

  const handleCloseImageCrop = () => {
    setOpenCropperModal(false);
  };

  const [_maxAllowedSize, _setMaxAllowedSize] = useState<number>(MAX_ALLOWED_SIZE_MB * 1024 * 1024);
  const [_allowedImageExtensions, _setAllowedImageExtensions] = useState<string[]>([
    "jpg",
    "jpeg",
    "png",
  ]);
  const [_logo, _setLogo] = useState<string>("");
  const [logoToCrop, setLogoToCrop] = useState<string>("");
  const [isUploading, setIsUploading] = useState<boolean>(false);

  useEffect(() => {
    _setLogo(logo?.mediaLink ?? "");
  }, [logo]);

  useEffect(() => {
    if (maxAllowedSizeMb) {
      _setMaxAllowedSize(maxAllowedSizeMb * 1024 * 1024);
    }
  }, [maxAllowedSizeMb]);

  useEffect(() => {
    if (allowedImageExtensions) {
      _setAllowedImageExtensions(allowedImageExtensions);
    }
  }, [allowedImageExtensions]);

  const logoIsValid = (logoFile?: IFile) => {
    return fileIsValid(translate, logoFile, {
      allowedExtensions: _allowedImageExtensions,
      maxSize: _maxAllowedSize,
    });
  };

  const handleChangeLogo = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target && event?.target?.files && event?.target?.files?.length > 0) {
      const logoValue = event?.target?.files[0];

      const type = logoValue.type.split("/");
      const extension = type.length > 0 ? type[1] : logoValue.type;

      const fileToSave = {
        extension,
        size: logoValue.size,
      } as IFile;

      if (logoValue && logoIsValid(fileToSave)) {
        const logo64 = await convertToBase64(logoValue);

        if (onChangeLogo && logo64) {
          const logoString = String(logo64).replace(base(extension), "");

          _setExtension(extension);

          if (cropImage) {
            setLogoToCrop(logo64 ? logo64.toString() : "");
            setOpenCropperModal(true);
          } else {
            _setLogo(logo64 ? logo64.toString() : "");

            if (uploadMode === "s3") {
              if (!logoString) {
                NotificationToast.error(translate("anErrorOccurredTryAgain"));
                return;
              }

              setIsUploading(true);

              uploadBase64ImageToS3(logoString, extension, {
                pathPrefix: uploadPathPrefix || "logo",
              })
                .then((link) => {
                  _setLogo(link);

                  if (logo) {
                    const newLogo: Logo = {
                      ...logo,
                      mediaLink: link,
                    };

                    delete newLogo?.createdAt;
                    delete newLogo?.updatedAt;

                    onChangeLogo(newLogo);
                  } else {
                    onChangeLogo({ mediaLink: link });
                  }
                })
                .catch((error) => {
                  console.error("Error uploading logo to S3", error);
                  NotificationToast.error(translate("anErrorOccurredTryAgain"));
                })
                .finally(() => {
                  setIsUploading(false);
                });
            } else {
              let newLogo: Logo = {};

              if (logo) {
                newLogo = {
                  ...logo,
                  mediaLink: logoString,
                };

                delete newLogo?.createdAt;
                delete newLogo?.updatedAt;

                onChangeLogo(newLogo);

                return;
              }

              onChangeLogo({ mediaLink: logoString });
            }
          }
        }
      }
      event.target.value = "";
    }
  };

  const renderLogo = () => {
    const randomNumber = Math.floor(Math.random() * 1000000);
    const logoSchema = _logo.split(":")[0];

    if (logoSchema === "https" || logoSchema === "http") {
      return `${_logo}?dummy${randomNumber}`;
    }

    if (isBase64(_logo, { allowMime: false, allowEmpty: false })) {
      return `data:image/png;base64,${_logo}`;
    }

    return _logo;
  };

  return (
    <>
      <main className={classes.main}>
        <span className={classes.label}>{title}</span>

        <section
          className={classes.section}
          style={{
            display: "flex",
            flexDirection: direction === "vertical" ? "column" : "row",
          }}
        >
          <div
            className={
              error
                ? classnames(classes.divLogo, classname, classes.onError)
                : classnames(classes.divLogo, classname)
            }
          >
            {_logo ? (
              <label htmlFor={id} className={classnames(classes.inputLogo, classes.imageContainer)}>
                <img src={renderLogo()} alt="logo-company" />
              </label>
            ) : (
              <label htmlFor={id} className={classes.inputLogo}>
                <FiUpload size={24} />

                <span>{description}</span>
              </label>
            )}

            <input
              ref={inputImageRef}
              id={id}
              disabled={disabled || isUploading}
              type="file"
              name="logoCompany"
              style={{ display: "none" }}
              accept={
                _allowedImageExtensions
                  ? _allowedImageExtensions?.map((extension) => `.${extension}`).join(", ")
                  : "image/*"
              }
              onChange={handleChangeLogo}
            />
          </div>

          {!hideFooter && (
            <div className={classes.divRulesLogo}>
              {!showActionButtons && <strong>{translate("choosePicture")}</strong>}
              <span>{`${translate("maxSize")} ${maxAllowedSizeMb || MAX_ALLOWED_SIZE_MB}MB`}</span>
              {cropAspectHeight && cropAspectWidth && (
                <span>{`${translate(
                  "maxImageSize"
                )} ${cropAspectHeight} X ${cropAspectWidth}`}</span>
              )}
              <span>{`${translate("formats")} ${_allowedImageExtensions
                .map((imageExtension) => imageExtension.toUpperCase())
                .join(", ")}`}</span>
              {showActionButtons && _logo && (
                <div className={classes.choosePictureButtonContainer}>
                  <Button
                    disabled={disabled}
                    customColor={Color.SECONDARY}
                    onClick={() => {
                      inputImageRef?.current?.click();
                    }}
                  >
                    {translate("changePicture")}
                  </Button>
                  <Button
                    disabled={disabled}
                    customColor={Color.CANCEL}
                    className={classes.removeButton}
                    onClick={() => {
                      _setLogo("");
                      onChangeLogo?.(undefined);
                    }}
                  >
                    {translate("remove")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </section>
      </main>
      <Modal
        title={translate("cropImage")}
        open={openCropperModal}
        handleClose={handleCloseImageCrop}
        showActionButtons
        actionButtonProps={{
          onSave: () => handleImageCrop(),
          textSave: translate("crop"),
          saveDisabled: isUploading,
        }}
      >
        <div className={classes.cropContainer}>
          <Cropper
            image={logoToCrop}
            crop={crop}
            zoom={zoom}
            aspect={cropAspect || (cropAspectWidth || 4) / (cropAspectHeight || 3)}
            cropShape={cropShape}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
      </Modal>
    </>
  );
};

export default ImageInput;
